import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Bot, Users, GitBranch, Inbox, BarChart3, Settings, LogOut, Sparkles, Search, Radar } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const NAV = [
  { to: "/dashboard", end: true, icon: LayoutDashboard, label: "Overview", tid: "nav-overview" },
  { to: "/dashboard/agents", icon: Bot, label: "AI Agents", tid: "nav-agents" },
  { to: "/dashboard/prospecting", icon: Search, label: "Prospecting", tid: "nav-prospecting" },
  { to: "/dashboard/leads", icon: Users, label: "Leads", tid: "nav-leads" },
  { to: "/dashboard/sequences", icon: GitBranch, label: "Sequences", tid: "nav-sequences" },
  { to: "/dashboard/inbox", icon: Inbox, label: "Inbox", tid: "nav-inbox" },
  { to: "/dashboard/signals", icon: Radar, label: "Signals", tid: "nav-signals" },
  { to: "/dashboard/analytics", icon: BarChart3, label: "Analytics", tid: "nav-analytics" },
  { to: "/dashboard/settings", icon: Settings, label: "Settings", tid: "nav-settings" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A0D14] text-slate-100 flex">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-white/5 bg-[#0B1020]/60 backdrop-blur-xl h-screen sticky top-0 flex flex-col">
        <button onClick={() => navigate("/")} className="px-5 h-16 flex items-center gap-2 border-b border-white/5" data-testid="sidebar-logo">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-emerald-400 grid place-items-center">
            <Bot className="w-4 h-4 text-slate-900" />
          </div>
          <span className="font-bold tracking-tight text-lg">AiPipe</span>
        </button>

        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`sidebar-${n.tid}`}
              className={({ isActive }) =>
                `flex items-center gap-3 h-10 px-3 rounded-lg text-sm transition ${
                  isActive ? "bg-white/[0.06] text-white" : "text-slate-400 hover:text-white hover:bg-white/[0.03]"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-white/5">
          <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3 mb-3">
            <div className="flex items-center gap-3">
              <Avatar className="w-9 h-9">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-sky-500/20 text-sky-200 text-xs">
                  {user?.name?.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{user?.name}</div>
                <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
              </div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="sidebar-logout-btn"
            className="w-full flex items-center gap-2 h-9 px-3 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-white/[0.04] transition"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="sticky top-0 z-30 h-14 border-b border-white/5 bg-[#0A0D14]/70 backdrop-blur-xl px-6 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-slate-400 font-mono">
            <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
            AI agents running · pipeline live
          </div>
          <div className="text-xs text-slate-500 font-mono uppercase tracking-widest">Trial · 14d left</div>
        </div>
        <div className="p-6 lg:p-8" data-testid="dashboard-outlet">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
